import VideoElement from "./VideoElement";
import ImageElement from "./ImageElement";
import TextElement from "./TextElement";
import MemoryCardsElement from "./MemoryCardsElement";
import ButtonElement from "./ButtonElement";
import TimerElement from "./TimerElement";
import CameraInputElement from "./CameraInputElement";
import CameraInputFilterElement from "./CameraInputFilterElement";
import FaceDetectionElement from "./FaceDetectionElement";
import Gamepad from "./Gamepad";
import QRCodeElement from "./QRCodeElement";
import InputElement from "./InputElement";
import ScreenshotElement from "./ScreenshotElement";
import FaceDetectionLandMarker from "./face-detection-landmarker";
import CarouselElement from "./CarouselElement";

import { isRotated, interpolateOpacity, takeScreenshot } from "../helpers";

import { useCallback, useMemo, useState } from "react";

function getStyle({ currentTime, player, element }) {
  const { timeline, style = {} } = element;
  const { top, bottom, left, right, height, width, zIndex, padding } = style;

  const rotated = isRotated() || (player && player.rotate);

  return {
    top: top,
    bottom: bottom,
    left: left,
    right: right,
    height: height,
    width: width,
    zIndex: zIndex,
    padding: padding,
    opacity: interpolateOpacity(currentTime, timeline),
    ...(rotated && { transform: `translateZ(${zIndex}em)` }),
  };
}

function Element({
  element,
  playing,
  player,
  currentTime,
  setActiveSlide,
  isSlideActive,
  creativeState,
  setCreativeState,
}) {
  const { config = {} } = element;
  const { state = {} } = config;
  const { visibility } = state;
  const { handlers: handlersList = [] } = config;
  const [isVisible, setIsVisible] = useState(
    visibility === "conditional" ? false : true,
  );

  const functions = useMemo(() => {
    return {
      changeActiveSlide: function ({ slide }) {
        return () => setActiveSlide(slide);
      },
      screenshot: function ({ canvasClass }) {
        const canvas = `.screenshot-canvas.screenshot-canvas--${canvasClass}`;
        const callback = (url) => {
          setCreativeState((prev) => ({ ...prev, faceTrackerImageUrl: url }));
        };
        return function () {
          return takeScreenshot(canvas, callback);
        };
      },
    };
  }, [setActiveSlide, setCreativeState]);

  function onAction() {
    const { action = {} } = element;

    const { type } = action;

    switch (type) {
      case "setSlide":
        const { targetSlide } = action;
        return setActiveSlide(targetSlide);
      default:
        return null;
    }
  }

  const getHandlers = useCallback(
    function () {
      if (!isVisible) {
        return {};
      }

      if (!handlersList.length) {
        return {};
      }

      return handlersList.reduce((handlers, trigger) => {
        if (!handlers[trigger.action]) {
          return {
            ...handlers,
            [trigger.action]: functions[trigger.function](trigger.payload),
          };
        }

        const composedHandler = function () {
          return (
            handlers[trigger.action]() ||
            functions[trigger.function](trigger.payload)()
          );
        };

        return {
          ...handlers,
          [trigger.action]: composedHandler,
        };
      }, {});
    },
    [handlersList, functions, isVisible],
  );

  function renderElementType(style) {
    const isActive = isSlideActive && style.opacity > 0;

    switch (element.type) {
      case "text":
        return <TextElement element={element} />;
      case "image":
        return (
          <ImageElement
            element={element}
            playing={playing}
            visible={style.opacity > 0}
            faceTrackerImageUrl={creativeState.faceTrackerImageUrl}
          />
        );
      case "video":
        return (
          <VideoElement
            element={element}
            player={player}
            visible={isSlideActive}
            playing={playing}
            onAction={onAction}
          />
        );
      case "buttonInteractive":
        return (
          <ButtonElement
            element={element}
            setActiveSlide={setActiveSlide}
            creativeState={creativeState}
          />
        );
      case "memoryElement":
        return (
          <MemoryCardsElement
            element={element}
            player={player}
            visible={style.opacity > 0}
            playing={playing}
            setActiveSlide={setActiveSlide}
            isSlideActive={isSlideActive}
          />
        );
      case "timer":
        return (
          <TimerElement
            element={element}
            playing={playing}
            visible={isSlideActive}
          />
        );
      case "cameraInput": {
        return (
          <CameraInputElement
            element={element}
            playing={playing}
            visible={isSlideActive}
            creativeState={creativeState}
            setCreativeState={setCreativeState}
            setActiveSlide={setActiveSlide}
          />
        );
      }
      case "cameraInputFilter": {
        return <CameraInputFilterElement element={element} />;
      }
      case "faceTracker": {
        return <FaceDetectionLandMarker element={element} />;
      }
      case "faceDetection": {
        return (
          <FaceDetectionElement
            element={element}
            onAction={onAction}
            isVisible={isActive}
          />
        );
      }
      case "gamepad": {
        return (
          <Gamepad
            element={element}
            onAction={onAction}
            isVisible={isActive}
            setActiveSlide={setActiveSlide}
          />
        );
      }
      case "qrcode":
        return (
          <QRCodeElement
            element={element}
            player={player}
            faceTrackerImageUrl={creativeState.faceTrackerImageUrl}
          />
        );
      case "input":
        return (
          <InputElement
            element={element}
            creativeState={creativeState}
            setCreativeState={setCreativeState}
            playing={playing}
            visible={isSlideActive}
          />
        );
      case "screenshot":
        return <ScreenshotElement element={element} />;
      case "carousel":
        return <CarouselElement element={element} player={player} />;
      default:
        return null;
    }
  }

  const style = getStyle({ currentTime, player, element });

  if (visibility === "conditional") {
    if (!isVisible && creativeState.faceTrackerEnabled) {
      return setIsVisible(true);
    }
  }

  if (isVisible === false) {
    return <></>;
  }

  return (
    <div className="element" style={style} {...getHandlers()}>
      {renderElementType(style)}
    </div>
  );
}

export default Element;
